/**
 * Quote/reply-to banner in the composer header (docs/composer-gap-analysis.md
 * section 13).
 *
 * One dismissible row above the editor: optional source label, the quoted text
 * clamped to a line, and a real button for dismissal so keyboard users reach it.
 */

import type { ComposerQuote } from "../types";
import { createElement, createNode } from "../utils/dom";
import { renderLucideIcon } from "../utils/icons";
import { summarizeQuoteText } from "../utils/composer-quote";

export interface ComposerQuoteBanner {
  element: HTMLElement;
  /** Repaint from the current quote; a no-op when nothing changed. */
  render: (quote: ComposerQuote | undefined) => void;
  destroy: () => void;
}

export function createComposerQuoteBanner(options: {
  onDismiss: () => void;
  dismissLabel?: string;
}): ComposerQuoteBanner {
  const element = createNode("div", {
    className: "persona-composer-quote",
    attrs: { "data-persona-composer-quote": "" },
  });
  element.style.display = "none";
  let painted = "";
  let destroyed = false;

  const build = (quote: ComposerQuote): void => {
    const bar = createElement("span", "persona-composer-quote-bar");
    const content = createElement("div", "persona-composer-quote-content");
    if (quote.sourceLabel) {
      const source = createElement("div", "persona-composer-quote-source");
      source.textContent = quote.sourceLabel;
      content.appendChild(source);
    }
    const text = createNode("div", {
      className: "persona-composer-quote-text",
      attrs: { "data-persona-composer-quote-text": "" },
    });
    text.textContent = summarizeQuoteText(quote.text);
    content.appendChild(text);

    const dismiss = createNode("button", {
      className: "persona-composer-quote-dismiss",
      attrs: {
        type: "button",
        "aria-label": options.dismissLabel ?? "Remove quote",
        "data-persona-composer-quote-dismiss": "",
      },
    }) as HTMLButtonElement;
    const icon = renderLucideIcon("x", 12, "currentColor", 2);
    if (icon) dismiss.appendChild(icon);
    else dismiss.textContent = "×";
    dismiss.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      options.onDismiss();
    });

    element.replaceChildren(bar, content, dismiss);
  };

  return {
    element,
    render: (quote) => {
      if (destroyed) return;
      const key = quote ? `${quote.sourceLabel ?? ""} ${quote.text}` : "";
      if (key === painted) return;
      painted = key;
      if (!quote) {
        element.replaceChildren();
        element.style.display = "none";
        return;
      }
      build(quote);
      element.style.display = "flex";
    },
    destroy: () => {
      destroyed = true;
      element.replaceChildren();
      element.remove();
    },
  };
}
